import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// ui_element_instances table; UiElementInstanceDto.From in lureo-backend Data/Api/V1/SceneContent/UiElementInstanceDtos.cs
export type UiElementInstanceDto = components['schemas']['UiElementInstanceDto'];
export type CreateUiElementInstanceRequest = components['schemas']['CreateUiElementInstanceRequest'];

const spec = {
  uniqueId: 'instance_id',
  sceneUniqueId: 'scene_instance_id',
  uiElementTypeUniqueId: 'ui_element_type_id',
  sourceName: 'source_name',
  category: 'category',
  displayName: 'display_name',
  textBody: 'text_body',
  maxCharacterCount: 'max_character_count',
  styleCornerRadius: 'style_corner_radius',
  styleBackgroundColor: 'style_background_color',
  styleFont: 'style_font',
  styleTextSize: 'style_text_size',
  styleTextColour: 'style_text_colour',
  styleBorder: 'style_border',
  styleBorderRadius: 'style_border_radius',
  styleBorderThickness: 'style_border_thickness',
  styleBorderColour: 'style_border_colour',
  styleAlignment: 'style_alignment',
  styleJustify: 'style_justify',
  availability: 'availability',
  isActive: 'is_active',
  // The entity holds uss as an object, not JSON text
  uss: { legacy: 'uss', as: 'json' },
  beginTime: 'begin_time',
  endTime: 'end_time',
  createdAt: 'created_at',
} as const satisfies FieldSpec<UiElementInstanceDto>;

const map = fieldMap<UiElementInstanceDto, typeof spec>('UI element instance', spec);

export type LegacyUiElementInstance = LegacyRow<typeof spec, UiElementInstanceDto>;

/** updateUiElementInstance accepts these. */
export const UI_ELEMENT_INSTANCE_PATCH_FIELDS = [
  'uiElementTypeUniqueId', 'sourceName', 'category', 'displayName', 'textBody', 'maxCharacterCount', 'styleCornerRadius',
  'styleBackgroundColor', 'styleFont', 'styleTextSize', 'styleTextColour', 'styleBorder', 'styleBorderRadius',
  'styleBorderThickness', 'styleBorderColour', 'styleAlignment', 'styleJustify', 'availability', 'isActive', 'uss',
  'beginTime', 'endTime',
] as const;
/** createUiElementInstance accepts these. */
export const UI_ELEMENT_INSTANCE_CREATE_FIELDS = ['uniqueId', 'sceneUniqueId', ...UI_ELEMENT_INSTANCE_PATCH_FIELDS] as const;

export const uiElementInstanceToLegacy = (dto: UiElementInstanceDto): LegacyUiElementInstance => map.toLegacy(dto);

/** The UiElementInstanceDto for a legacy row read from v1, for messages that carry the v1 shape. */
export const uiElementInstanceFromLegacy = (row: LegacyUiElementInstance): UiElementInstanceDto => map.toDto(row);

/** createUiElementInstance body from a legacy insert row; instance_id is required. The author is the caller's membership. */
export function uiElementInstanceCreateBody(row: Partial<LegacyUiElementInstance>): CreateUiElementInstanceRequest {
  const body = map.toBody(row, UI_ELEMENT_INSTANCE_CREATE_FIELDS, ['created_at']);
  return { ...body, uniqueId: requireId('UI element instance', 'instance_id', body.uniqueId) };
}

/** updateUiElementInstance body from a legacy partial update. */
export const uiElementInstancePatchBody = (changes: Partial<LegacyUiElementInstance>) => map.toBody(changes, UI_ELEMENT_INSTANCE_PATCH_FIELDS);
